import { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Link,
  TextField,
  InputAdornment,
  TableSortLabel,
  Select,
  MenuItem,
  FormControl,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { toast } from 'react-toastify';
import propertiesServices from '../../../services/propertiesServices';

const STATUSES = ['Active', 'Pending', 'Completed', 'Cold', 'Cancelled', 'Lost'];

const getStatusColor = (status) => {
  const statusLower = status?.toLowerCase() || '';
  if (statusLower === 'completed') return '#2e7d32';
  if (statusLower === 'active') return '#1976d2';
  if (statusLower === 'pending') return '#ed6c02';
  if (statusLower === 'cold') return '#9e9e9e';
  if (statusLower === 'cancelled') return '#d32f2f';
  if (statusLower === 'lost') return '#e91e63';
  return '#757575';
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return dateString; }
};

const formatPrice = (price) => {
  if (!price || price === 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

// Mobile Legacy Proposal Card
const MobileLegacyCard = ({ proposal, onStatusChange, updating }) => (
  <Card variant="outlined" sx={{ mb: 2, borderLeft: `4px solid ${getStatusColor(proposal.status)}` }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Link href={proposal.url} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {proposal.proposalNumber}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
        <Typography variant="caption" color="text.secondary">{formatDate(proposal.createdAt)}</Typography>
      </Box>
      
      {/* Price */}
      {proposal.price > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, bgcolor: '#e8f5e9', p: 1, borderRadius: 1 }}>
          <AttachMoneyIcon sx={{ fontSize: 18, color: '#2e7d32' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2e7d32' }}>
            {formatPrice(proposal.price)}
          </Typography>
        </Box>
      )}
      
      {/* Status Dropdown */}
      <FormControl size="small" fullWidth>
        <Select
          value={proposal.status || ''}
          onChange={(e) => onStatusChange(proposal.proposalNumber, e.target.value)}
          disabled={updating === proposal.proposalNumber}
          sx={{ '& .MuiSelect-select': { py: 1, color: getStatusColor(proposal.status), fontWeight: 500 } }}
        >
          {STATUSES.map((status) => (
            <MenuItem key={status} value={status} sx={{ color: getStatusColor(status), fontWeight: 500 }}>{status}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </CardContent>
  </Card>
);

const PropertyLegacyProposalsTable = ({ legacyProposals = [], propertyId, refresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [updating, setUpdating] = useState(null);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleStatusChange = async (proposalNumber, newStatus) => {
    setUpdating(proposalNumber);
    try {
      await propertiesServices.updateLegacyProposalStatus(propertyId, proposalNumber, newStatus);
      if (refresh) await refresh();
      toast.success('Status updated');
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const filteredAndSortedProposals = useMemo(() => {
    let result = [...legacyProposals];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((p) => p.proposalNumber?.toLowerCase().includes(query) || p.status?.toLowerCase().includes(query));
    }
    result.sort((a, b) => {
      let aVal = a[orderBy], bVal = b[orderBy];
      if (orderBy === 'createdAt') { aVal = aVal ? new Date(aVal).getTime() : 0; bVal = bVal ? new Date(bVal).getTime() : 0; }
      else if (orderBy === 'price') { aVal = parseFloat(aVal) || 0; bVal = parseFloat(bVal) || 0; }
      else { aVal = String(aVal || '').toLowerCase(); bVal = String(bVal || '').toLowerCase(); }
      return order === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
    return result;
  }, [legacyProposals, searchQuery, orderBy, order]);

  if (!legacyProposals || legacyProposals.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, color: 'text.secondary' }}>
        <HistoryIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
        <Typography variant="body2">No legacy proposals found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TextField size="small" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        sx={{ width: isMobile ? '100%' : 300, mb: 2 }}
      />

      {/* Desktop Table */}
      {!isMobile && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell><TableSortLabel active={orderBy === 'proposalNumber'} direction={orderBy === 'proposalNumber' ? order : 'asc'} onClick={() => handleSort('proposalNumber')}>Proposal #</TableSortLabel></TableCell>
                <TableCell><TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? order : 'asc'} onClick={() => handleSort('status')}>Status</TableSortLabel></TableCell>
                <TableCell><TableSortLabel active={orderBy === 'price'} direction={orderBy === 'price' ? order : 'asc'} onClick={() => handleSort('price')}>Price</TableSortLabel></TableCell>
                <TableCell><TableSortLabel active={orderBy === 'createdAt'} direction={orderBy === 'createdAt' ? order : 'asc'} onClick={() => handleSort('createdAt')}>Created</TableSortLabel></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedProposals.map((proposal, index) => (
                <TableRow key={`${proposal.proposalNumber}-${index}`} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                  <TableCell><Link href={proposal.url} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 500 }}>{proposal.proposalNumber}</Link></TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select value={proposal.status || ''} onChange={(e) => handleStatusChange(proposal.proposalNumber, e.target.value)} disabled={updating === proposal.proposalNumber}
                        sx={{ '& .MuiSelect-select': { py: 0.5, color: getStatusColor(proposal.status), fontWeight: 500 } }}>
                        {STATUSES.map((status) => (<MenuItem key={status} value={status} sx={{ color: getStatusColor(status), fontWeight: 500 }}>{status}</MenuItem>))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: proposal.price ? 600 : 400, color: proposal.price ? 'success.main' : 'text.secondary' }}>
                      {formatPrice(proposal.price)}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{formatDate(proposal.createdAt)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobile Cards */}
      {isMobile && (
        <Box>
          {filteredAndSortedProposals.map((proposal, index) => (
            <MobileLegacyCard key={`${proposal.proposalNumber}-${index}`} proposal={proposal} onStatusChange={handleStatusChange} updating={updating} />
          ))}
        </Box>
      )}

      {filteredAndSortedProposals.length === 0 && searchQuery && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No proposals match your search</Typography>
      )}
    </Box>
  );
};

export default PropertyLegacyProposalsTable;
